/* eslint-disable no-nested-ternary */
/*
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, useGlobalFilter, useAsyncDebounce, useSortBy, useRowSelect } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";

// Material Dashboard 2 PRO React examples
import DataTableBodyCell from "components/DataTable/DataTableBodyCell";

function DataTable({
  // Behavior switches
  canSearch,
  canSelect,
  canActuate,
  canGlobalAction,
  noEndBorder,
  table,
  selectedIndex,
  // Actions
  action,
  globalAction,
  globalActionText,
  globalActionColor,
  onSelectionChange,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Columns & rows
  const columns = useMemo(() => table.columns, [JSON.stringify(table.columns)]);
  const data = useMemo(() => table.rows, [JSON.stringify(table.rows)]);

  // Table instance (no pagination hook -> we render all rows)
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: "lastMessage.createdAt", // sort by the "When" column
            desc: false, // newest first
          },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    useRowSelect
  );

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    rows,
    selectedFlatRows,
    setGlobalFilter,
    state: { globalFilter },
  } = tableInstance;

  useEffect(() => {
    if (!onSelectionChange) return;
    onSelectionChange(selectedFlatRows.map((row) => row.original));
  }, [onSelectionChange, selectedFlatRows]);

  // Search state
  const [search, setSearch] = useState(globalFilter || "");
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  return (
    <MDBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Top bar: Search + Global Action */}
      {canSearch && (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {/* Search (no entries-per-page control) */}
          {canSearch ? (
            <MDBox width={canGlobalAction ? "calc(100% - 170px)" : "100%"}>
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          ) : (
            <span />
          )}

          {canGlobalAction && (
            <MDButton
              color={globalActionColor}
              variant="contained"
              onClick={globalAction}
              disabled={selectedFlatRows.length === 0}
              sx={{ ml: 2, whiteSpace: "nowrap" }}
            >
              {globalActionText}
            </MDButton>
          )}
        </MDBox>
      )}

      {/* Table */}
      <TableContainer sx={{ boxShadow: "none", flex: 1, overflowY: "auto" }}>
        <Table {...getTableProps()}>
          <TableBody {...getTableBodyProps()}>
            {rows.map((row, key) => {
              prepareRow(row);

              const lastMessageDeleted = Boolean(row.original?.lastMessage?.deleted);
              const unread =
                !lastMessageDeleted &&
                (row.original?.lastRead?.master ?? 0) < (row.original?.lastMessage?.createdAt ?? 0);

              return (
                <TableRow
                  selected={row.index === selectedIndex}
                  {...row.getRowProps()}
                  onClick={() => {
                    if (canActuate) action(row);
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: unread ? "info.main" : "transparent",
                    },
                    ...(unread
                      ? {
                          backgroundColor: "info.main",
                          "& td, & td *, & .MuiTypography-root": {
                            color: "white !important",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "info.main",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "info.main",
                          },
                          "&.Mui-selected td, &.Mui-selected td *, &.Mui-selected .MuiTypography-root":
                            {
                              color: "white !important",
                            },
                        }
                      : {
                          "&.Mui-selected": {
                            backgroundColor: "info.selected",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "info.selected",
                          },
                        }),
                  }}
                >
                  {canSelect ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        {...row.getToggleRowSelectedProps()}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </TableCell>
                  ) : (
                    <TableCell padding="checkbox" />
                  )}

                  {row.cells.map((cell) => (
                    <DataTableBodyCell
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cell.getCellProps()}
                      sx={{
                        fontSize: 16,
                        fontWeight: 400,
                        color: unread ? "white" : darkMode ? "white" : "inherit",
                      }}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
}

// Default props
DataTable.defaultProps = {
  canSearch: false,
  canSelect: false,
  canEdit: false,
  canAdd: false,
  canActuate: false,
  canGlobalAction: false,
  selectedIndex: 0,
  canCustomActuate: false,
  showTotalEntries: true,
  noEndBorder: false,
  add: () => {},
  edit: () => {},
  remove: () => {},
  action: () => {},
  customAction: () => {},
  customActionIcon: "download",
  globalAction: () => {},
  globalActionIcon: "send",
  globalActionColor: "dark",
  globalActionText: "",
  onSelectionChange: null,
};

// Prop types
DataTable.propTypes = {
  canSearch: PropTypes.bool,
  canSelect: PropTypes.bool,
  canEdit: PropTypes.bool,
  canAdd: PropTypes.bool,
  canActuate: PropTypes.bool,
  canGlobalAction: PropTypes.bool,
  canCustomActuate: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  add: PropTypes.func,
  edit: PropTypes.func,
  remove: PropTypes.func,
  action: PropTypes.func,
  selectedIndex: PropTypes.number,
  customAction: PropTypes.func,
  customActionIcon: PropTypes.string,
  globalAction: PropTypes.func,
  globalActionIcon: PropTypes.string,
  globalActionColor: PropTypes.string,
  globalActionText: PropTypes.string,
  onSelectionChange: PropTypes.func,
};

export default DataTable;
